import React, { useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  User,
  Clock,
  Calendar,
  GraduationCap,
  Award,
  Users,
  X,
  ExternalLink,
  FileText,
  Image,
  Video,
  Link,
  ChevronDown,
  ChevronUp,
  Download,
} from "lucide-react";
import { toast } from "react-hot-toast";
import ReactPlayer from "react-player";
import DocViewer from "react-doc-viewer";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";
import { getUpcomingExams } from "../../services/exams";
import { courseMaterialService } from "../../services/courseMaterialService";

export const StudentCourses: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [showMaterialsModal, setShowMaterialsModal] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set()
  );
  const [activeDocViewer, setActiveDocViewer] = useState<string | null>(null);

  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-courses"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  const {
    data: materialsData,
    isLoading: materialsLoading,
    refetch: refetchMaterials,
  } = useQuery({
    queryKey: ["course-materials", selectedCourse?._id],
    queryFn: () =>
      courseMaterialService.getCourseMaterials(selectedCourse._id, true),
    enabled: !!selectedCourse && showMaterialsModal,
  });

  // Fetch upcoming exams
  const { data: upcomingExamsData, isLoading: examsLoading } = useQuery({
    queryKey: ["upcoming-exams"],
    queryFn: () => getUpcomingExams(),
    enabled: !!user?.id && user?.roles?.includes("student"),
  });

  // Show notifications for upcoming exams
  useEffect(() => {
    if (upcomingExamsData && upcomingExamsData.length > 0) {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const urgentExams = upcomingExamsData.filter((exam: any) => {
        const examDate = new Date(exam.schedule.date);
        return examDate <= tomorrow && examDate >= today;
      });

      if (urgentExams.length > 0) {
        toast(
          `You have ${urgentExams.length} exam(s) scheduled for today or tomorrow!`,
          {
            icon: "📅",
            duration: 5000,
          }
        );
      }
    }
  }, [upcomingExamsData]);

  const handleViewMaterials = (courseId: string) => {
    const course = courses.find((c: any) => c._id === courseId);
    setSelectedCourse(course);
    setShowMaterialsModal(true);
    setExpandedModules(new Set()); // Reset expanded modules
    setActiveDocViewer(null); // Reset active doc viewer
  };

  const toggleModule = (moduleId: string) => {
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const toggleDocViewer = (materialId: string) => {
    setActiveDocViewer((prev) => (prev === materialId ? null : materialId));
  };

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;

    // Handle youtu.be format
    const youtuBeMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (youtuBeMatch) return youtuBeMatch[1];

    // Handle youtube.com/watch?v= format
    const youtubeMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
    if (youtubeMatch) return youtubeMatch[1];

    // Handle youtube.com/embed/ format
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) return embedMatch[1];

    return null;
  };

  // Check if URL is a YouTube link
  const isYouTubeUrl = (url: string): boolean => {
    return url.includes("youtube.com") || url.includes("youtu.be");
  };

  // Check if a file is a PDF
  const isPDF = (fileUrl: string): boolean => {
    if (!fileUrl) return false;
    const extension = fileUrl.split(".").pop()?.toLowerCase();
    return extension === "pdf";
  };

  // Check if a file can be previewed (images, text files)
  const canPreviewDocument = (fileUrl: string): boolean => {
    if (!fileUrl) return false;
    const extension = fileUrl.split(".").pop()?.toLowerCase();
    // Previewable formats (excluding PDF which is handled separately)
    const supportedExtensions = [
      "png",
      "jpg",
      "jpeg",
      "gif",
      "bmp",
      "txt",
      "csv",
    ];
    return supportedExtensions.includes(extension || "");
  };

  // Get file extension from URL
  const getFileExtension = (fileUrl: string): string => {
    return fileUrl.split(".").pop()?.toLowerCase() || "";
  };

  const student = studentData?.data;

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Regular courses are not available for e-course students.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }

  const courses = student?.coursesInfo || [];
  const totalCourses = courses.length;
  const activeCourses = courses.filter(
    (course: any) => course.isActive !== false
  ).length;
  const completedCourses = student?.academicRecords?.length || 0;

  const renderMainContent = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 px-0">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
          My Courses
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-lg md:text-xl">
          View your enrolled courses, progress, and academic information
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Courses */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Total Courses
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {totalCourses}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <BookOpen className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Active Courses */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Active Courses
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {activeCourses}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <GraduationCap className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Completed</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {completedCourses}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <Award className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Current Class */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Current Class
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {student?.classInfo?.[0]?.name || "N/A"}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg">
              <Users className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Courses */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 py-4 px-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              Current Enrollments
            </h2>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
          </div>

          {courses && courses.length > 0 ? (
            <div className="space-y-4">
              {courses.map((course: any, index: number) => (
                <div
                  key={course._id || `course-${index}`}
                  className="border border-slate-200/60 rounded-xl p-4 bg-gradient-to-br from-slate-50 to-blue-50 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                        <BookOpen className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {course.name || "Course Name"}({course.level})
                        </h3>
                        <p className="text-sm text-slate-600">
                          {course.code || "No code"}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md">
                      Enrolled
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-700 font-medium">
                        {course.instructor || "TBA"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-700 font-medium">
                        {course.credits || "N/A"} Credits
                      </span>
                    </div>
                  </div>

                  {course.description && (
                    <p className="text-sm text-slate-600 mt-3 line-clamp-2">
                      {course.description}
                    </p>
                  )}

                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <button
                      onClick={() => handleViewMaterials(course._id)}
                      className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      View Materials
                    </button>
                  </div>

                  {course.feeStructure && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">
                            $
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 font-medium">
                          Course Fee: Ksh{" "}
                          {course.feeStructure.perPeriodAmount || 0}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                <BookOpen className="h-8 w-8 text-slate-500" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">
                No courses enrolled
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                You haven't enrolled in any courses yet.
              </p>
            </div>
          )}
        </div>

        {/* Academic Progress */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              Academic Progress
            </h2>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Award className="h-5 w-5 text-white" />
            </div>
          </div>

          {student?.academicRecords && student.academicRecords.length > 0 ? (
            <div className="space-y-4">
              {student.academicRecords.map((record: any, index: number) => (
                <div
                  key={record._id || `record-${index}`}
                  className="border border-slate-200/60 rounded-xl p-4 bg-gradient-to-br from-slate-50 to-purple-50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">
                      {record.academicTerm || `Term ${index + 1}`}
                    </h3>
                    <div className="px-3 py-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 shadow-md">
                      <span className="text-sm font-bold text-white">
                        GPA: {record.gpa?.toFixed(2) || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {record.creditsEarned || 0}
                        </span>
                      </div>
                      <span className="text-slate-700 font-medium">
                        Credits
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                      <span className="text-slate-700 font-medium">
                        {record.status || "In Progress"}
                      </span>
                    </div>
                  </div>
                  {record.achievements && record.achievements.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-2">
                        Achievements:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {record.achievements.map(
                          (achievement: string, idx: number) => (
                            <span
                              key={idx}
                              className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-md"
                            >
                              {achievement}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                <Award className="h-8 w-8 text-slate-500" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">
                No academic records
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Your academic progress will appear here once available.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Upcoming Events</h2>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
            <Calendar className="h-5 w-5 text-white" />
          </div>
        </div>

        {examsLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-slate-200 border-t-blue-600 mx-auto"></div>
            <p className="text-sm text-slate-600 mt-3 font-medium">
              Loading events...
            </p>
          </div>
        ) : upcomingExamsData && upcomingExamsData.length > 0 ? (
          <div className="space-y-4">
            {upcomingExamsData.map((exam: any) => (
              <div
                key={exam._id}
                className="border border-slate-200/60 rounded-xl p-4 bg-gradient-to-br from-slate-50 to-green-50 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">
                      {exam.title}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {exam.courseId?.name} ({exam.courseId?.code})
                    </p>
                  </div>
                  <span
                    className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full shadow-md ${
                      exam.type === "online"
                        ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                        : "bg-gradient-to-r from-orange-500 to-yellow-500 text-white"
                    }`}
                  >
                    {exam.type === "online" ? "Online Quiz" : "Physical Exam"}
                  </span>
                </div>

                <div className="flex items-center space-x-4 text-sm mt-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-700 font-medium">
                      {new Date(exam.schedule.date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-700 font-medium">
                      {exam.schedule.startTime} - {exam.schedule.endTime}
                    </span>
                  </div>
                </div>

                {exam.venue && exam.type === "physical" && (
                  <div className="flex items-center space-x-2 text-sm mt-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">📍</span>
                    </div>
                    <span className="text-slate-700 font-medium">
                      {exam.venue}
                    </span>
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm text-slate-700 font-medium">
                        {exam.schedule.duration} min
                      </span>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 shadow-md">
                      <span className="text-sm font-bold text-white">
                        {exam.maxMarks} marks
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-slate-500" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              No upcoming events
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              No exams or quizzes scheduled for the next 7 days.
            </p>
          </div>
        )}
      </div>

      {/* Course Schedule (if available) */}
      {student?.classInfo?.[0] && (
        <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              Class Information
            </h2>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-white" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200/60">
              <label className="block text-sm font-semibold text-slate-600 mb-2">
                Class Name
              </label>
              <p className="text-slate-900 font-bold text-lg">
                {student.classInfo[0].name}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-green-50 border border-slate-200/60">
              <label className="block text-sm font-semibold text-slate-600 mb-2">
                Capacity
              </label>
              <p className="text-slate-900 font-bold text-lg">
                {student.classInfo[0].capacity || "N/A"} students
              </p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-purple-50 border border-slate-200/60">
              <label className="block text-sm font-semibold text-slate-600 mb-2">
                Schedule
              </label>
              <p className="text-slate-900 font-bold text-lg">
                {student.classInfo[0].schedule || "TBA"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">
            Error Loading Course Information
          </h3>
          <p className="text-red-600 mt-1">
            Unable to load your course details. Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  // Materials Modal
  const renderMaterialsModal = () => {
    if (!showMaterialsModal) return null;

    const modules = materialsData || [];

    const handleCloseModal = () => {
      setShowMaterialsModal(false);
      setExpandedModules(new Set());
      setActiveDocViewer(null);
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden border border-slate-200/60">
          <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                Course Materials - {selectedCourse?.name}
              </h2>
            </div>
            <button
              onClick={handleCloseModal}
              className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400 flex items-center justify-center transition-all duration-300 hover:scale-110"
            >
              <X className="h-5 w-5 text-slate-700" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[75vh] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {materialsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : modules.length > 0 ? (
              <div className="space-y-4">
                {modules.map((module: any) => {
                  const isExpanded = expandedModules.has(module._id);
                  const materialCount = module.materials?.length || 0;

                  return (
                    <div
                      key={module._id}
                      className="border border-slate-200/60 rounded-xl overflow-hidden bg-white/95 backdrop-blur-xl shadow-lg"
                    >
                      <button
                        onClick={() => toggleModule(module._id)}
                        className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 flex items-center justify-between hover:from-blue-100 hover:to-indigo-100 transition-all duration-300"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                            <BookOpen className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-slate-900">
                              {module.name}
                            </h3>
                            {module.description && (
                              <p className="text-sm text-slate-600 mt-1">
                                {module.description}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 mt-2">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                                {materialCount}{" "}
                                {materialCount === 1 ? "material" : "materials"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-slate-700" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-700" />
                          )}
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50">
                          {module.materials && module.materials.length > 0 ? (
                            <div className="space-y-4">
                              {module.materials.map(
                                (material: any, index: number) => (
                                  <div
                                    key={material._id || `material-${index}`}
                                    className="border border-slate-200/60 rounded-xl p-5 bg-white/90 hover:shadow-lg transition-all duration-300"
                                  >
                                    <div className="flex items-start justify-between mb-4">
                                      <div className="flex items-start space-x-3 flex-1">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md bg-gradient-to-br from-blue-500 to-indigo-500">
                                          {material.type === "document" && (
                                            <FileText className="h-5 w-5 text-white" />
                                          )}
                                          {material.type === "video" && (
                                            <Video className="h-5 w-5 text-white" />
                                          )}
                                          {material.type === "image" && (
                                            <Image className="h-5 w-5 text-white" />
                                          )}
                                          {material.type === "link" && (
                                            <Link className="h-5 w-5 text-white" />
                                          )}
                                          {material.type === "text" && (
                                            <FileText className="h-5 w-5 text-white" />
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <h4 className="font-semibold text-slate-900">
                                            {material.title}
                                          </h4>
                                          {material.description && (
                                            <p className="text-sm text-slate-600 mt-1">
                                              {material.description}
                                            </p>
                                          )}
                                          <div className="flex items-center space-x-3 mt-2">
                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                                              {material.type}
                                            </span>
                                            <span className="text-xs text-slate-500">
                                              {new Date(
                                                material.createdAt
                                              ).toLocaleDateString()}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      {material.fileUrl && (
                                        <a
                                          href={material.fileUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                        >
                                          <ExternalLink className="h-4 w-4 mr-2" />
                                          Open
                                        </a>
                                      )}
                                    </div>

                                    {/* Content Display */}
                                    <div className="mt-4">
                                      {material.type === "text" &&
                                        material.content && (
                                          <div className="p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200/60">
                                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                                              {material.content}
                                            </p>
                                          </div>
                                        )}

                                      {material.type === "image" &&
                                        material.fileUrl && (
                                          <div className="flex justify-center p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200/60">
                                            <img
                                              src={material.fileUrl}
                                              alt={material.title}
                                              className="max-w-full max-h-96 object-contain rounded-md"
                                            />
                                          </div>
                                        )}

                                      {material.type === "video" &&
                                        material.fileUrl && (
                                          <div className="w-full bg-black rounded-lg overflow-hidden">
                                            <ReactPlayer
                                              url={material.fileUrl}
                                              controls={true}
                                              width="100%"
                                              height="400px"
                                              config={{
                                                file: {
                                                  attributes: {
                                                    controlsList: "nodownload",
                                                  },
                                                },
                                              }}
                                            />
                                          </div>
                                        )}

                                      {material.type === "link" &&
                                        material.fileUrl && (
                                          <div className="w-full">
                                            {isYouTubeUrl(material.fileUrl) ? (
                                              // YouTube videos - use native iframe
                                              <div className="w-full">
                                                <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                                                  <iframe
                                                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                                                      material.fileUrl
                                                    )}?modestbranding=1&rel=0&showinfo=0&controls=1&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=1&color=white&disablekb=0&enablejsapi=1&playsinline=1`}
                                                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    title={material.title}
                                                  />
                                                </div>
                                              </div>
                                            ) : (
                                              // Non-YouTube links
                                              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                                                <a
                                                  href={material.fileUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                                                >
                                                  {material.fileUrl}
                                                </a>
                                              </div>
                                            )}
                                          </div>
                                        )}

                                      {material.type === "document" &&
                                        material.fileUrl && (
                                          <div className="w-full">
                                            {isPDF(material.fileUrl) ? (
                                              // PDF files - use iframe for reliable rendering
                                              activeDocViewer ===
                                              material._id ? (
                                                <div>
                                                  <button
                                                    onClick={() =>
                                                      setActiveDocViewer(null)
                                                    }
                                                    className="mb-3 px-4 py-2 text-sm font-semibold text-slate-700 bg-gradient-to-r from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    Close Preview
                                                  </button>
                                                  <iframe
                                                    src={material.fileUrl}
                                                    className="w-full border border-slate-300 rounded-xl shadow-lg"
                                                    style={{ height: "600px" }}
                                                    title={material.title}
                                                  />
                                                </div>
                                              ) : (
                                                <div className="flex items-center space-x-3">
                                                  <button
                                                    onClick={() =>
                                                      toggleDocViewer(
                                                        material._id
                                                      )
                                                    }
                                                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    <FileText className="h-4 w-4 mr-2" />
                                                    Preview PDF
                                                  </button>
                                                  <a
                                                    href={material.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700 bg-gradient-to-r from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                    Open in New Tab
                                                  </a>
                                                </div>
                                              )
                                            ) : canPreviewDocument(
                                                material.fileUrl
                                              ) ? (
                                              // Previewable documents (images, txt)
                                              activeDocViewer ===
                                              material._id ? (
                                                <div>
                                                  <button
                                                    onClick={() =>
                                                      setActiveDocViewer(null)
                                                    }
                                                    className="mb-3 px-4 py-2 text-sm font-semibold text-slate-700 bg-gradient-to-r from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    Close Preview
                                                  </button>
                                                  <DocViewer
                                                    key={material._id}
                                                    documents={[
                                                      { uri: material.fileUrl },
                                                    ]}
                                                    config={{
                                                      header: {
                                                        disableHeader: true,
                                                        disableFileName: true,
                                                      },
                                                    }}
                                                    style={{ height: 500 }}
                                                    pluginRenderers={[]}
                                                  />
                                                </div>
                                              ) : (
                                                <div className="flex items-center space-x-3">
                                                  <button
                                                    onClick={() =>
                                                      toggleDocViewer(
                                                        material._id
                                                      )
                                                    }
                                                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    <FileText className="h-4 w-4 mr-2" />
                                                    Preview Document
                                                  </button>
                                                  <a
                                                    href={material.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700 bg-gradient-to-r from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                    Open in New Tab
                                                  </a>
                                                </div>
                                              )
                                            ) : (
                                              // Non-previewable documents (Word, Excel, PowerPoint)
                                              <div className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200/60 rounded-xl">
                                                <div className="flex items-start space-x-3">
                                                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                                                    <FileText className="h-5 w-5 text-white" />
                                                  </div>
                                                  <div className="flex-1">
                                                    <p className="text-sm text-yellow-900 font-semibold">
                                                      This file type (.
                                                      {getFileExtension(
                                                        material.fileUrl
                                                      )}
                                                      ) cannot be previewed in
                                                      the browser
                                                    </p>
                                                    <p className="text-xs text-yellow-800 mt-1">
                                                      Please download or open in
                                                      a new tab to view
                                                    </p>
                                                  </div>
                                                </div>
                                                <div className="flex items-center space-x-3 mt-4">
                                                  <a
                                                    href={material.fileUrl}
                                                    download
                                                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    <Download className="h-4 w-4 mr-2" />
                                                    Download File
                                                  </a>
                                                  <a
                                                    href={material.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700 bg-gradient-to-r from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400 rounded-lg transition-all duration-300 hover:scale-105 shadow-md"
                                                  >
                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                    Open in New Tab
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                                <FileText className="h-7 w-7 text-slate-500" />
                              </div>
                              <p className="text-sm font-semibold text-slate-900">
                                No materials in this module yet.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                  <BookOpen className="h-10 w-10 text-slate-500" />
                </div>
                <h3 className="text-base font-bold text-slate-900">
                  No materials available
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  This course doesn't have any materials uploaded yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderMainContent()}
      {renderMaterialsModal()}
    </>
  );
};
